import { supabase } from '../../../services/supabase/client'
import { FONT_FACES, PURPOSE_OPTIONS, parseBrandLayout } from '../../branding/types'
import type { BrandLayout, BrandPreset, BrandSnapshot } from '../../branding/types'
import type { ReportBrand, ReportToggles } from '../components/ReportDocument'
import type { Json } from '../../../types/database.types'

/**
 * Resolution order (spec 003, plan §5): the report draft's explicit choice →
 * the client's default preset → the org default → none (legacy output).
 *
 * Every lookup is best-effort: offline or failing queries degrade to the next
 * candidate and finally to an unbranded PDF rather than blocking generation —
 * report generation must keep working offline exactly as it does today.
 */
export async function resolveBrandPreset(opts: {
  reportData: Record<string, unknown> | null | undefined
  clientPresetId: string | null
  orgId: string | null
}): Promise<BrandPreset | null> {
  const explicitId =
    typeof opts.reportData?.brand_preset_id === 'string' && opts.reportData.brand_preset_id
      ? opts.reportData.brand_preset_id
      : null

  for (const presetId of [explicitId, opts.clientPresetId]) {
    if (!presetId) continue
    try {
      const { data } = await supabase
        .from('brand_presets')
        .select('*')
        .eq('id', presetId)
        .maybeSingle()
      if (data) return data
    } catch {
      /* degrade to the next candidate */
    }
  }

  if (opts.orgId) {
    try {
      const { data } = await supabase
        .from('brand_presets')
        .select('*')
        .eq('org_id', opts.orgId)
        .eq('is_default', true)
        .maybeSingle()
      if (data) return data
    } catch {
      /* degrade to unbranded */
    }
  }
  return null
}

/** The layout a report actually renders with: per-report override, else the
    preset's. An override never carries a section order (010 R2) — the report
    keeps its own in report_data.section_order. */
export function effectiveLayout(
  preset: BrandPreset,
  reportData: Record<string, unknown> | null | undefined
): BrandLayout {
  const override = reportData?.layout_override
  if (override && typeof override === 'object' && !Array.isArray(override)) {
    return { ...parseBrandLayout(override as Json), section_order: null }
  }
  return parseBrandLayout(preset.layout)
}

export const purposeLabel = (purpose: BrandLayout['purpose']): string =>
  PURPOSE_OPTIONS.find((o) => o.value === purpose)?.label ?? 'Full assessment'

/**
 * Purpose maps deterministically onto the existing section toggles (US3
 * AC-2) — it only ever turns existing keys off, never invents one, and only
 * on top of what the user already enabled.
 *
 * - full_assessment: every enabled section, untouched.
 * - summary: findings, node table and actions; methodology stays enabled but
 *   the renderer moves it to an appendix.
 * - client_brief: plain language and photo-led — no technical appendices.
 */
export function applyPurposeToToggles(
  toggles: ReportToggles,
  purpose: BrandLayout['purpose']
): ReportToggles {
  switch (purpose) {
    case 'summary':
      return {
        ...toggles,
        include_scope: false,
        include_vta: false,
        include_traq: false,
        include_health_vigor: false,
        include_observations: false,
        include_tpp: false,
        include_references: false,
        include_additional_notes: false,
      }
    case 'client_brief':
      return {
        ...toggles,
        include_methodology: false,
        include_vta: false,
        include_traq: false,
        include_health_vigor: false,
        include_observations: false,
        include_tpp: false,
        include_references: false,
        include_tpz_diagram: false,
      }
    default:
      return toggles
  }
}

export function buildReportBrand(opts: {
  preset: BrandPreset
  layout: BrandLayout
  logoDataUrl: string | null
  logoLightDataUrl: string | null
  profile: {
    company_name: string | null
    abn: string | null
    phone: string | null
    qualifications: string[] | null
    certifications: string[] | null
  } | null
}): ReportBrand {
  const { preset, layout, profile } = opts
  const face = FONT_FACES.find((f) => f.family === preset.body_font)
  const accreditationLine = preset.show_accreditation
    ? [profile?.qualifications?.[0], profile?.certifications?.[0]].filter(Boolean).join(' · ') ||
      null
    : null

  return {
    presetId: preset.id,
    presetName: preset.name,
    presetVersion: preset.version,
    primaryColor: preset.primary_color,
    accentColor: preset.accent_color,
    headingFont: preset.heading_font,
    bodyFont: preset.body_font,
    bodyFontHasItalic: face?.hasItalic ?? false,
    logoUrl: opts.logoDataUrl,
    logoLightUrl: opts.logoLightDataUrl,
    useKnockout: preset.use_knockout,
    cover: layout.cover,
    purposeLabel: purposeLabel(layout.purpose),
    layout,
    orgName: profile?.company_name ?? null,
    abn: profile?.abn ?? null,
    phone: profile?.phone ?? null,
    accreditationLine,
  }
}

/** The immutable record written to reports.metadata.brand at generation time. */
export function brandSnapshot(brand: ReportBrand): BrandSnapshot {
  return {
    preset_id: brand.presetId,
    preset_name: brand.presetName,
    preset_version: brand.presetVersion,
    primary_color: brand.primaryColor,
    accent_color: brand.accentColor,
    heading_font: brand.headingFont,
    body_font: brand.bodyFont,
    layout: brand.layout,
  }
}
